import { RichText, RichTextField } from '@sitecore-jss/sitecore-jss-nextjs';
import ExpandableContent from 'components/Generic/ExpandableContent';
import { useEffect, useState } from 'react';
import { useI18n } from 'next-localization';

const ICONS = {
  warning: 'exclamation-circle',
  error: 'exclamation-triangle',
  success: 'check-circle',
  informative: 'info-circle',
  generic: undefined,
} as const;

export type IconType = keyof typeof ICONS;

type AlertMessageBaseProps = {
  type?: IconType;
  title?: string;
  description?: RichTextField;
  expandableDescription?: RichTextField;
  defaultExpanded?: boolean;
  showIcon?: boolean;
  className?: string;
};

const AlertMessageBase = ({
  title,
  description,
  expandableDescription,
  defaultExpanded = false,
  type = 'generic',
  showIcon = true,
  className = '',
}: AlertMessageBaseProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { t } = useI18n();
  const icon = ICONS[type];
  const renderIcon: boolean = !!icon && showIcon;
  const showDropdownButton: boolean = !!expandableDescription?.value;

  useEffect(() => {
    const elContentChildren = document.querySelector('.alert-message-base__content')?.children;
    const lastElContentChildren = elContentChildren?.item(elContentChildren.length - 1);

    // is list element ?
    if (
      !lastElContentChildren ||
      !['ul', 'ol'].includes(lastElContentChildren.tagName.toLowerCase())
    )
      return;

    const elMoreContent = document.querySelector('.alert-message-base__more-content');
    const firstMoreContentEl = elMoreContent?.children.item(0);

    // check same el
    if (lastElContentChildren.tagName !== firstMoreContentEl?.tagName) return;

    // check 2nd ol list continue with order number lik 1-2..3-4-5
    if (
      ['ol'].includes(lastElContentChildren.tagName.toLowerCase()) &&
      parseInt(firstMoreContentEl.getAttribute('start') as string) !==
        lastElContentChildren.children.length + 1
    )
      return;

    (lastElContentChildren as HTMLElement).style.marginBottom = '0';
    elMoreContent?.classList?.remove('pt-1');
  }, [isExpanded]);

  return (
    <div
      className={`alert-message-base alert alert--${type} ${
        renderIcon ? 'p-2' : 'px-1.5 py-1'
      } ${className}`}
    >
      <h2 className="sr-only">{t('alertImportantInformation')}</h2>

      <div className="d-flex gap-2">
        {renderIcon && (
          <div className="flex-shrink-0 icon-wrapper icon-wrapper--size-4">
            <i className={`bi bi-${icon}`} aria-hidden="true" />
          </div>
        )}
        <div className="field-text align-self-center">{title}</div>
      </div>

      {(description?.value || expandableDescription?.value) && (
        <div className={`mt-1 content-wrapper ${renderIcon ? 'with-padding' : ''}`}>
          <RichText field={description} className="body-default alert-message-base__content" />
          <ExpandableContent show={isExpanded}>
            <RichText
              field={expandableDescription}
              className="pt-1 body-default alert-message-base__more-content"
            />
          </ExpandableContent>
          {showDropdownButton && (
            <button
              aria-expanded={isExpanded ? 'true' : 'false'}
              className="mt-1 btn-naked d-flex align-items-center gap-1"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <div className="text-link">{t(isExpanded ? 'showLess' : 'showMore')}</div>
              <div className="icon-wrapper icon-wrapper--size-3">
                <i className="bi bi-chevron-down" aria-hidden="true" />
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertMessageBase;
